import { isAddedShippingCountry, INTERNATIONAL_SHIPPING_POLICY } from "@/lib/international-shipping";

export interface ContextMoney { amount: string; currencyCode: string }

/** Plain decimal strings stay exact until the final display formatting boundary. */
function decimalParts(amount: string): { units: bigint; scale: number } | null {
  if (amount.length > 100 || !/^\d+(?:\.\d+)?$/.test(amount)) return null;
  const [whole, fraction = ""] = amount.split(".");
  return { units: BigInt(whole + fraction), scale: fraction.length };
}

function decimalAmount(units: bigint, scale: number): string {
  const digits = String(units).padStart(scale + 1, "0");
  return scale ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits;
}

function sumMoney(money: ContextMoney[]): ContextMoney | null {
  const currencyCode = money[0]?.currencyCode;
  if (!money.length || !money.every(m => validMoney(m) && m.currencyCode === currencyCode)) return null;
  const parts = money.map(m => decimalParts(m.amount));
  let scale = 0;
  for (const part of parts) {
    if (!part) return null;
    scale = Math.max(scale, part.scale);
  }
  let units = 0n;
  for (const part of parts) {
    if (!part) return null;
    units += part.units * 10n ** BigInt(scale - part.scale);
  }
  return { amount: decimalAmount(units, scale), currencyCode };
}

/** Multiply without binary floating-point tails, preserving currency and decimal scale. */
export function multiplyContextMoney(money: ContextMoney, quantity: number): ContextMoney | null {
  if (!validMoney(money) || !Number.isSafeInteger(quantity) || quantity < 0) return null;
  const parts = decimalParts(money.amount);
  if (!parts) return null;
  return { amount: decimalAmount(parts.units * BigInt(quantity), parts.scale), currencyCode: money.currencyCode };
}

/** Currency precision is display-only; never a conversion or a price relabelling. */
export function formatContextMoney(money: ContextMoney, locale: string): string {
  const precision = new Intl.NumberFormat("en", { style: "currency", currency: money.currencyCode })
    .resolvedOptions().maximumFractionDigits;
  const formatter = new Intl.NumberFormat(locale === "ar" ? "ar" : "en", {
    style: "decimal", maximumFractionDigits: precision,
  });
  return `${formatter.format(Number(money.amount))} ${money.currencyCode}`;
}

export function contextualCurrency(requested: string, returned: unknown, currency: unknown): string {
  return requested === returned && typeof currency === "string" && /^[A-Z]{3}$/.test(currency) ? currency : "";
}

function validMoney(value: any): value is ContextMoney {
  return !!value && typeof value.amount === "string" && decimalParts(value.amount) !== null &&
    typeof value.currencyCode === "string" && /^[A-Z]{3}$/.test(value.currencyCode);
}

/** No FX, relabelling, missing lines, or cross-country adoption. */
export function reconcileContextCart<T extends { lineId: string | null; variantId: string; quantity: number; price: ContextMoney; isSampleGift?: boolean }>(
  cart: any, cartId: string, country: string, items: T[], giftCountry?: string,
): { items: T[]; subtotal: ContextMoney; shipping: ContextMoney | null } | null {
  if (!cart || cart.id !== cartId || !cart.checkoutUrl || cart.buyerIdentity?.countryCode !== country ||
    cart.resolvedCountry !== country || (giftCountry && giftCountry !== country)) return null;
  const subtotal = cart.cost?.subtotalAmount;
  if (!validMoney(subtotal)) return null;
  const lines = new Map<string, any>((cart.lines?.edges ?? []).map((e: any) => [e.node.id, e.node]));
  if (lines.size !== items.length || !items.length) return null;
  const next: T[] = [];
  for (const item of items) {
    const line = item.lineId ? lines.get(item.lineId) : null;
    const price = line?.cost?.amountPerQuantity;
    if (!line || line.quantity !== item.quantity || line.merchandise?.id !== item.variantId ||
      !validMoney(price) || price.currencyCode !== subtotal.currencyCode ||
      (item.isSampleGift && Number(price.amount) !== 0)) return null;
    next.push({ ...item, price: { ...price } });
  }
  const groups = cart.deliveryGroups?.edges;
  if (Array.isArray(groups) && groups.some((e: any) => e.node?.deliveryAddress?.countryCodeV2 &&
    e.node.deliveryAddress.countryCodeV2 !== country)) return null;
  let shipping: ContextMoney | null = null;
  if (Array.isArray(groups) && groups.length && !cart.deliveryGroups?.pageInfo?.hasNextPage) {
    const quotes = groups.map((e: any) => e.node?.selectedDeliveryOption?.estimatedCost);
    if (quotes.every((q: any) => validMoney(q) && q.currencyCode === subtotal.currencyCode)) {
      shipping = sumMoney(quotes);
    }
  }
  return { items: next, subtotal: { ...subtotal }, shipping };
}

/** An unverified or mixed-currency sum must not be displayed as a money amount. */
export function cartSubtotal(items: Array<{ price: ContextMoney; quantity: number }>, authoritative: ContextMoney | null): ContextMoney | null {
  const currency = items[0]?.price.currencyCode;
  if (!items.length || !items.every(i => validMoney(i.price) && i.price.currencyCode === currency)) return null;
  if (authoritative?.currencyCode === currency && validMoney(authoritative)) return authoritative;
  const lines = items.map(i => multiplyContextMoney(i.price, i.quantity));
  if (lines.some(line => line === null)) return null;
  return sumMoney(lines.filter((line): line is ContextMoney => line !== null));
}

export function productDeliveryPolicy(country: string): { key: string; vars?: Record<string, string>; installments: boolean } {
  if (country === "US" || isAddedShippingCountry(country)) return {
    key: "legal.shipping.added_timing", vars: {
      minDays: String(INTERNATIONAL_SHIPPING_POLICY.minBusinessDays), maxDays: String(INTERNATIONAL_SHIPPING_POLICY.maxBusinessDays),
    }, installments: false,
  };
  const keys: Record<string, string> = {
    AE: "legal.shipping.same_day_detail", OM: "legal.shipping.same_day_detail", QA: "legal.shipping.same_day_detail",
    SA: "legal.shipping.sa_time", IQ: "legal.shipping.iq_time",
  };
  return { key: keys[country] ?? "product.delivery_at_checkout", installments: country === "AE" || country === "SA" };
}